import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo } from 'react';
import { TouchableOpacity } from 'react-native';
import { designRoles } from './designRoles';

export type FeedActionTone = 'primary' | 'secondary' | 'danger';

const kTones: Record<
  FeedActionTone,
  { bg: string; border: string; text: string }
> = {
  primary: {
    bg: designRoles.interactive,
    border: designRoles.interactive,
    text: designRoles.textInverse,
  },
  secondary: {
    bg: designRoles.bgSurface,
    border: designRoles.borderStrong,
    text: designRoles.textSecondary,
  },
  danger: {
    bg: designRoles.bgSurface,
    border: designRoles.borderStrong,
    text: designRoles.statusDanger,
  },
};

/**
 * A message card's compact action, as the feed's cards draw theirs (as wide
 * as its words, beside its siblings): primary in the interactive blue,
 * secondary and danger outlined.
 */
export const FeedActionButton = memo(
  ({
    title,
    tone = 'primary',
    disabled,
    onPress,
  }: {
    title: string;
    tone?: FeedActionTone;
    disabled?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const colors = kTones[tone];
    return (
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityState={{ disabled: !!disabled }}
        activeOpacity={0.7}
        disabled={disabled}
        onPress={onPress}
        style={{
          minHeight: rpx(32),
          paddingHorizontal: rpx(14),
          borderRadius: rpx(6),
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.bg,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <Typography.Text size={rpx(13)} color={colors.text} medium>
          {title}
        </Typography.Text>
      </TouchableOpacity>
    );
  },
);
